import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { X, Bell, Menu, ChevronDown, LogOut, User, MessageSquare } from "lucide-react";

const userMenuItems = [
  { id: "account", label: "My Account", icon: User },
  { id: "messages", label: "My Messages", icon: MessageSquare },
];

const TopBar = ({
  isMobile,
  sidebarCollapsed,
  setSidebarOpen,
  isNotificationOpen,
  setIsNotificationOpen,
  notifications,
  notificationLoading,
  notificationError,
  toggleNotifications,
  fetchNotifications,
  markNotificationAsRead,
  clearAllNotifications,
  isUserMenuOpen,
  setIsUserMenuOpen,
  user,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const pageTitle = location.pathname.replace("/", "").replace(/-/g, " ") || "dashboard";

  const navigateTo = (pageId) => {
    navigate(`/${pageId}`);
    setIsUserMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully");
    } catch {
      toast.error("Error logging out");
    }
  };

  return (
    <header className={`fixed top-0 right-0 z-30 h-16 bg-white border-b border-gray-200 shadow-sm transition-all duration-300 ${isMobile ? 'left-0' : (sidebarCollapsed ? 'left-16' : 'left-64')}`}>
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
        >
          <Menu size={20} />
        </button>

        {/* Page Title - Desktop */}
        <div className="hidden lg:block">
          <h1 className="text-xl font-semibold text-gray-900 capitalize">
            {pageTitle}
          </h1>
        </div>

        {/* Right Side - Notifications & User Menu */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative notification-container">
            <button
              onClick={toggleNotifications}
              className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 active:bg-gray-200"
              aria-label="Notifications"
            >
              <Bell size={20} />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute top-1 right-1 block h-2 w-2 bg-blue-500 rounded-full animate-pulse"></span>
              )}
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                  {notifications.filter(n => !n.read).length > 9 ? '9+' : notifications.filter(n => !n.read).length}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {isNotificationOpen && (
              <div
                className={`bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden ${isMobile ? 'fixed' : 'absolute right-0 w-80 mt-2'}`}
                style={isMobile ? { position: 'fixed', left: '1rem', right: '1rem', top: '4rem', width: 'auto', marginTop: 0 } : {}}
              >
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                  <div className="flex items-center space-x-2">
                    {notifications.length > 0 && (
                      <button onClick={clearAllNotifications} className="text-xs text-blue-600 hover:text-blue-800 transition-colors">
                        Clear all
                      </button>
                    )}
                    {isMobile && (
                      <button onClick={() => setIsNotificationOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="max-h-72 overflow-y-auto">
                  {notificationLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      <span className="ml-2 text-sm text-gray-600">Loading...</span>
                    </div>
                  ) : notificationError ? (
                    <div className="px-4 py-8 text-center">
                      <div className="text-red-500 text-sm mb-2">⚠️ {notificationError}</div>
                      <button onClick={fetchNotifications} className="text-xs text-blue-600 hover:text-blue-800 transition-colors">
                        Try again
                      </button>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      <Bell size={24} className="mx-auto mb-2 text-gray-300" />
                      No notifications
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => markNotificationAsRead(notification.id)}
                          className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${!notification.read ? 'bg-blue-50' : ''}`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                              notification.type === 'success' ? 'bg-green-500' :
                              notification.type === 'warning' ? 'bg-yellow-500' :
                              notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
                            }`}></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {notification.title}
                                </p>
                                {!notification.read && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>}
                              </div>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{new Date(notification.timestamp).toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                    <button
                      onClick={() => setIsNotificationOpen(false)}
                      className="text-xs text-blue-600 hover:text-blue-800 transition-colors w-full text-center"
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative user-menu-container">
            <button
              onClick={(e) => { e.stopPropagation(); setIsUserMenuOpen(!isUserMenuOpen); }}
              className="flex items-center space-x-3 p-2 text-sm rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <img
                className="h-8 w-8 rounded-full border-2 border-gray-200"
                src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=00C5CB&color=fff`}
                alt={user.name}
              />
              <div className="text-left hidden sm:block">
                <div className="font-medium text-gray-900">{user.name}</div>
                <div className="text-xs text-gray-500">{user.role}</div>
              </div>
              <ChevronDown size={16} className="text-gray-400 hidden sm:block" />
            </button>

            {isUserMenuOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                <div className="py-2">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="font-medium text-gray-900">{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className="text-xs font-medium text-blue-600">{user.role}</div>
                  </div>

                  {userMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => navigateTo(item.id)}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon size={16} className="mr-3 text-gray-400" />
                        {item.label}
                      </button>
                    );
                  })}

                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} className="mr-3" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
